/**
 * Slice 40 — Pre-Clean maintenance check (due today).
 * Done needs proof photo; Skip always works. Live simple turnover chrome.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import {
  isMaintenanceChecksComplete,
  readSimpleFlowState,
  type SimpleFlowState,
} from "@/lib/simpleTurnover";
import {
  appendPhotoCaptureRecord,
  captureFrameFromVideo,
  uploadInAppCleaningPhoto,
} from "@/lib/cleaningPhotoCapture";
import { simpleFlowSegmentIndex } from "@/lib/turnoverWizardPlan";
import { SimpleStepHeader } from "@/components/cleaner/SimpleStepHeader";
import { CLEANER_SIMPLE_CTA_FOOTER, CLEANER_SIMPLE_STEP_SHELL } from "@/lib/turnoverChrome";
import type { DueMaintenanceCheck } from "@/hooks/useDueMaintenanceChecks";
import { MediaThumb } from "@/components/media/MediaThumb";
import { getNextScheduledDate, type PreventativeFrequency } from "@/hooks/usePreventativeMaintenance";

interface Props {
  propertyId: string;
  taskId: string;
  entryWalkthroughState: unknown;
  dueChecks: DueMaintenanceCheck[];
  includeQuickCheck?: boolean;
  includeGuestSetup?: boolean;
  onComplete: () => void;
  onBack?: () => void;
  onSelectSegment?: (index: number) => void;
}

export function SimpleMaintenanceDueStep({
  propertyId,
  taskId,
  entryWalkthroughState,
  dueChecks,
  includeQuickCheck = false,
  includeGuestSetup = false,
  onComplete,
  onBack,
  onSelectSegment,
}: Props) {
  const { t, tDynamic } = useTranslation();
  const initial = readSimpleFlowState(entryWalkthroughState);
  const pending = useMemo(() => {
    const map = initial.maintenance_checks ?? {};
    return dueChecks.filter((c) => {
      const row = map[c.id];
      return !row || (row.status !== "done" && row.status !== "skipped");
    });
  }, [dueChecks, initial.maintenance_checks]);

  const current = pending[0] ?? dueChecks[0] ?? null;
  const [mode, setMode] = useState<"card" | "camera">("card");
  const [proofUrl, setProofUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const shutterLock = useRef(false);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch {
      setCameraError(t("couldNotOpenCamera"));
      setCameraReady(false);
    }
  }, []);

  useEffect(() => {
    if (mode !== "camera") {
      stopCamera();
      return;
    }
    void startCamera();
    return () => stopCamera();
  }, [mode, startCamera, stopCamera]);

  const persistCheck = async (
    checkId: string,
    title: string,
    status: "done" | "skipped",
    proof: string | null,
  ) => {
    const prev = readSimpleFlowState(entryWalkthroughState);
    const nextMap = {
      ...(prev.maintenance_checks ?? {}),
      [checkId]: { status, proof_url: proof, title },
    };
    const nextState: SimpleFlowState = {
      ...prev,
      simple_flow: true,
      maintenance_checks: nextMap,
      maintenance_complete: isMaintenanceChecksComplete(
        { ...prev, maintenance_checks: nextMap },
        dueChecks.map((c) => c.id),
      ),
    };
    const { error } = await supabase
      .from("cleaning_tasks")
      .update({ entry_walkthrough_state: nextState })
      .eq("id", taskId);
    if (error) throw error;

    if (status === "done") {
      const nextDue = getNextScheduledDate(
        (dueChecks.find((c) => c.id === checkId)?.frequency as PreventativeFrequency) || "monthly",
        new Date(),
      );
      await supabase
        .from("property_preventative_maintenance")
        .update({ next_scheduled_date: nextDue })
        .eq("id", checkId);
    }

    return nextState;
  };

  const onSkip = async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      const next = await persistCheck(current.id, current.title, "skipped", null);
      if (isMaintenanceChecksComplete(next, dueChecks.map((c) => c.id))) {
        onComplete();
      } else {
        setProofUrl(null);
        setMode("card");
        onComplete(); // parent invalidates → next pending
      }
    } catch (err) {
      toast({
        title: t("couldntSkip"),
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const onCapture = async () => {
    if (!current || shutterLock.current || !videoRef.current) return;
    shutterLock.current = true;
    setBusy(true);
    try {
      const frame = await captureFrameFromVideo(videoRef.current);
      const { url, record } = await uploadInAppCleaningPhoto({
        propertyId,
        taskId,
        moment: "walk",
        blob: frame.blob,
        width: frame.width,
        height: frame.height,
        storagePath: `${propertyId}/${taskId}/maintenance/${crypto.randomUUID()}.jpg`,
      });
      const prev = readSimpleFlowState(entryWalkthroughState);
      await supabase
        .from("cleaning_tasks")
        .update({
          entry_walkthrough_state: {
            ...prev,
            photo_captures: appendPhotoCaptureRecord(prev.photo_captures, record),
          },
        })
        .eq("id", taskId);
      setProofUrl(url);
      setMode("card");
    } catch (err) {
      toast({
        title: t("captureFailed"),
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      shutterLock.current = false;
      setBusy(false);
    }
  };

  const onDone = async () => {
    if (!current || !proofUrl || busy) return;
    setBusy(true);
    try {
      const next = await persistCheck(current.id, current.title, "done", proofUrl);
      setProofUrl(null);
      if (isMaintenanceChecksComplete(next, dueChecks.map((c) => c.id))) {
        onComplete();
      } else {
        onComplete();
      }
    } catch (err) {
      toast({
        title: t("couldNotSave"),
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  if (!current) {
    onComplete();
    return null;
  }

  if (mode === "camera") {
    return (
      <div className={cn(CLEANER_SIMPLE_STEP_SHELL, "bg-[#0f1720]")}>
        <div className="flex items-center justify-between px-5 pt-3">
          <button
            type="button"
            onClick={() => setMode("card")}
            className="text-[16px] font-bold text-white/80"
          >
            {t("cancel")}
          </button>
          <div className="text-[16px] font-bold text-white">{t("proofPhoto")}</div>
          <div className="w-14" />
        </div>
        <div className="relative mx-5 mt-3 min-h-0 flex-1 overflow-hidden rounded-[18px] bg-black">
          <video ref={videoRef} playsInline muted autoPlay className="absolute inset-0 h-full w-full object-cover" />
          {!cameraReady ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/70">
              <p className="text-[15px] font-medium">{cameraError || t("startingCamera")}</p>
              <button
                type="button"
                onClick={() => void startCamera()}
                className="rounded-full bg-[#24a89a] px-5 py-3 text-[15px] font-bold text-white"
              >
                {t("openCamera")}
              </button>
            </div>
          ) : null}
        </div>
        <p className="px-5 pt-3 text-center text-[14px] font-medium text-white/70">
          {t("showFinishedWork")}
        </p>
        <div className="flex justify-center px-5 py-5">
          <button
            type="button"
            disabled={busy || !cameraReady}
            onClick={() => void onCapture()}
            aria-label={t("capture")}
            className="flex h-[84px] w-[84px] items-center justify-center rounded-full bg-[#24a89a] shadow-[0_6px_18px_rgba(36,168,154,0.35)] active:scale-95 disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-7 w-7 animate-spin text-white" />
            ) : (
              <Camera className="h-7 w-7 text-white" strokeWidth={2} />
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={CLEANER_SIMPLE_STEP_SHELL} data-tour="tour-simple-maintenance-due">
      <SimpleStepHeader
        title={tDynamic(current.title)}
        activeSegment={simpleFlowSegmentIndex("maintenance", {
          includeMaintenance: true,
          includeQuickCheck,
        })}
        includeMaintenance
        includeQuickCheck={includeQuickCheck}
        includeGuestSetup={includeGuestSetup}
        onBack={onBack}
        onSelectSegment={onSelectSegment}
        subtitle={
          <div className="text-[13px] font-bold tracking-[0.3px] text-[#b45309]">
            {t("dueTodayBeforeClean")}
          </div>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-5 pb-2">
        {current.photoUrl ? (
          <div className="overflow-hidden rounded-[16px] bg-[#eff4f7]">
            <div className="aspect-[4/3] w-full">
              <MediaThumb url={current.photoUrl} />
            </div>
            <div className="px-3 py-2 text-[12px] font-medium text-[#8494a2]">
              {t("hostReferencePhoto")}
            </div>
          </div>
        ) : null}

        <p className="text-[15px] font-medium text-[#64748b]">
          {t("doneNeedsPhoto")}
        </p>

        <button
          type="button"
          onClick={() => setMode("camera")}
          className={cn(
            "flex min-h-[120px] flex-col items-center justify-center gap-2 overflow-hidden rounded-[16px] border-[1.5px] border-dashed",
            proofUrl ? "border-[#24a89a] bg-[#e2eeed]" : "border-[#c5d4dc] bg-[#f4f7f9]",
          )}
        >
          {proofUrl ? (
            <div className="h-full w-full">
              <MediaThumb url={proofUrl} />
              <div className="px-3 py-2 text-center text-[13px] font-bold text-[#15776d]">
                {t("proofCapturedRetake")}
              </div>
            </div>
          ) : (
            <>
              <Camera className="h-8 w-8 text-[#24a89a]" strokeWidth={1.8} />
              <div className="text-[15px] font-bold text-[#475569]">{t("photoRequired")}</div>
            </>
          )}
        </button>
      </div>

      <div className={cn(CLEANER_SIMPLE_CTA_FOOTER, "flex flex-col gap-2.5")}>
        <button
          type="button"
          disabled={busy || !proofUrl}
          onClick={() => void onDone()}
          className={cn(
            "flex h-[58px] w-full items-center justify-center rounded-[15px] text-[18px] font-bold",
            proofUrl ? "bg-[#24a89a] text-white" : "bg-[#e8edf0] text-[#a2b0b9]",
          )}
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("done")}
        </button>
        {!proofUrl ? (
          <p className="text-center text-[13px] font-medium text-[#8494a2]">
            {t("doneNeedsPhoto")}
          </p>
        ) : null}
        <button
          type="button"
          disabled={busy}
          onClick={() => void onSkip()}
          className="flex h-12 w-full items-center justify-center rounded-[14px] text-[16px] font-bold text-[#64748b]"
        >
          {t("skip")}
        </button>
      </div>
    </div>
  );
}
